import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ResourcesTable from "../../components/manage-resources/resources-table";
import DeleteResourceModal from "../../components/manage-resources/delete-resource-modal";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

function ManageResources() {
  const location = useLocation();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const loadResources = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      if (isMountedRef.current) setError("Authentication required.");
      return;
    }
    if (isMountedRef.current) { setIsLoading(true); setError(null); }
    try {
      const res = await fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_RESOURCES}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json().catch(() => null);
      if (!res.ok) throw new Error(result?.message || "Unable to fetch resources.");
      if (isMountedRef.current) setResources(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      if (isMountedRef.current) { setError(err.message); setResources([]); }
      toast.error(err.message || "Unable to fetch resources.");
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResources();
    if (location.state?.refresh) navigate(location.pathname, { replace: true });
  }, [loadResources, location.pathname, location.state?.refresh, navigate]);

  const handleDelete = async () => {
    if (!selectedResource) return;
    const token = localStorage.getItem("accessToken");
    setIsDeleting(true);
    try {
      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_RESOURCES}/${selectedResource.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const result = await res.json().catch(() => null);
        throw new Error(result?.message || "Failed to delete resource.");
      }
      toast.success("Resource deleted.");
      setIsDeleteOpen(false);
      setSelectedResource(null);
      loadResources();
    } catch (err) {
      toast.error(err.message || "Failed to delete resource.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-[90%] lg:w-[95%] mx-auto mt-8">
      <ResourcesTable
        data={resources}
        onDelete={(r) => { setSelectedResource(r); setIsDeleteOpen(true); }}
        isLoading={isLoading}
        errorMessage={error}
      />
      {isDeleteOpen && selectedResource && (
        <DeleteResourceModal
          resource={selectedResource}
          onClose={() => { setIsDeleteOpen(false); setSelectedResource(null); }}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}

export default ManageResources;
